import React from 'react'
import { Button } from '@/components/ui/button'

interface EmptyStateProps {
  projectId: string
  projectName: string
}

export const EmptyState = ({ projectId, projectName }: EmptyStateProps) => {
  // Generate random positions for lightning strikes
  const lightningBolts = React.useMemo(() => {
    // Create pairs of lightning strikes - first strike at 0, 0.5, then 8, 8.5 seconds
    return [0, 0.5, 8, 8.5].map((delay, index) => ({
      delay,
      leftPosition: 15 + (Math.floor(index / 2) * 50) + Math.random() * 20,
      randomOffset: Math.random() * 40 - 20
    }))
  }, [])

  // Generate random rain drops
  const rainDrops = React.useMemo(() => {
    return Array.from({ length: 150 }, (_, i) => ({
      left: Math.random() * 100,
      animationDelay: Math.random() * 1,
      animationDuration: 0.3 + Math.random() * 0.3
    }))
  }, [])

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 border rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-8 h-[80vh] relative overflow-hidden">
      {/* Rain animation - continuous background */}
      <div className="absolute inset-0 pointer-events-none">
        {rainDrops.map((drop, index) => (
          <div
            key={`rain-${index}`}
            className="absolute w-[2px] h-[15px] bg-[#FF4A8E]/25 animate-[rain_linear_infinite]"
            style={{
              left: `${drop.left}%`,
              animationDelay: `${drop.animationDelay}s`,
              animationDuration: `${drop.animationDuration}s`
            }}
          />
        ))}
      </div>

      {/* Lightning animation container */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Multiple lightning bolts with different delays and positions */}
        {lightningBolts.map((bolt, index) => (
          <div
            key={index}
            className="absolute top-0 w-[80px] h-full opacity-0 animate-[lightning_16s_ease-out_infinite]"
            style={{
              left: `${bolt.leftPosition}%`,
              animationDelay: `${bolt.delay}s`,
              transform: `translateX(${bolt.randomOffset}px)`
            }}
          >
            {/* Lightning bolt path */}
            <svg
              className="w-full h-full"
              viewBox="0 0 80 100"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 0 L35 8 L45 12 L30 20 L50 28 L25 36 L55 44 L20 52 L60 60 L15 68 L50 76 L30 84 L45 92 L40 100"
                stroke="#FF4A8E"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
                className="drop-shadow-[0_0_8px_rgba(255,74,142,0.3)]"
              />
              {/* Secondary, thinner bolt for more detail */}
              <path
                d="M40 0 L38 10 L42 15 L36 25 L44 30 L34 40 L46 45 L32 55 L48 65 L35 75 L43 85 L40 100"
                stroke="#FF4A8E"
                strokeWidth="1"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.2"
              />
            </svg>
            {/* Glow effect */}
            <div className="absolute inset-0 w-[100px] -left-[10px] bg-[#FF4A8E]/10 blur-xl" />
          </div>
        ))}
      </div>

      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes lightning {
            0%, 94%, 100% {
              opacity: 0;
              transform: translateY(-100%) scaleY(0);
            }
            94.5% {
              opacity: 0;
              transform: translateY(-100%) scaleY(0);
            }
            95% {
              opacity: 0.25;
              transform: translateY(-100%) scaleY(0.1);
            }
            96% {
              opacity: 0.5;
              transform: translateY(-50%) scaleY(0.5);
            }
            97% {
              opacity: 0.8;
              transform: translateY(0) scaleY(1);
            }
            98% {
              opacity: 0.4;
              transform: translateY(0) scaleY(1);
            }
            98.5%, 100% {
              opacity: 0;
              transform: translateY(0) scaleY(1);
            }
          }
          
          @keyframes rain {
            from {
              transform: translateY(-100vh);
            }
            to {
              transform: translateY(100vh);
            }
          }
        `
      }} />

      {/* Pain Points icon */}
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 relative z-10">
        <svg className="w-8 h-8 text-[#FF4A8E]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M5.8333 15C4.79353 15 3.79634 14.6049 3.06112 13.9017C2.32589 13.1984 1.91284 12.2446 1.91284 11.25C1.91284 10.2554 2.32589 9.30161 3.06112 8.59835C3.79634 7.89509 4.79353 7.5 5.8333 7.5C6.07887 6.40598 6.79727 5.44457 7.83045 4.82726C8.34203 4.5216 8.9155 4.30962 9.51811 4.20343C10.1207 4.09723 10.7407 4.0989 11.3426 4.20833C11.9444 4.31777 12.5165 4.53283 13.026 4.84123C13.5356 5.14964 13.9726 5.54535 14.3123 6.00577C14.6519 6.4662 14.8874 6.98232 15.0054 7.52466C15.1234 8.06701 15.1216 8.62496 15 9.16667H15.8333C16.6068 9.16667 17.3487 9.47396 17.8957 10.0209C18.4427 10.5679 18.75 11.3098 18.75 12.0833C18.75 12.8569 18.4427 13.5987 17.8957 14.1457C17.3487 14.6927 16.6068 15 15.8333 15H15" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M10.8333 11.666L9.16663 14.9993H11.6666L9.99996 18.3327" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 relative z-10">
        No pain points collected yet
      </h3>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-6 relative z-10">
        Pain points will be automatically collected from your meetings and conversations. They help identify key challenges and issues that need to be addressed.
      </p>

      {/* <div className="flex flex-col gap-4 relative z-10">
        <Button
          onClick={() => window.location.href = '/dashboard/meetings'}
          className="bg-[#FF4A8E] hover:bg-[#ff2c7d] text-white"
        >
          Go to Meetings
        </Button>
      </div> */}
    </div>
  )
} 